import { useState, useEffect } from 'react';
import { useDebug } from '../context/DebugContext';
import './LoadSim.css';

interface SimulationConfig {
  targetTps: number;
  duration: number;
  payloadSize: number;
  concurrency: number;
}

interface SimulationResult {
  actualTps: number;
  avgLatencyMs: number;
  p99LatencyMs: number;
  errorRate: number;
  totalEvents: number;
  estimatedCost: number;
}

export default function LoadSim() {
  const { addEvent } = useDebug();
  const [config, setConfig] = useState<SimulationConfig>({
    targetTps: 1000,
    duration: 60,
    payloadSize: 256,
    concurrency: 10,
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Load Simulation page',
    });
  }, [addEvent]);

  const estimateCost = () => {
    const totalEvents = config.targetTps * config.duration;
    const bytesPerEvent = config.payloadSize + 64; // payload + overhead
    const totalBytes = totalEvents * bytesPerEvent;
    const costPerGB = 0.10; // $0.10 per GB
    return (totalBytes / 1e9) * costPerGB;
  };

  const handleStart = () => {
    addEvent({
      source: 'console',
      category: 'api',
      message: 'Starting load simulation',
      data: { ...config },
    });

    setRunning(true);
    setProgress(0);
    setResult(null);

    // Simulate progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 100 / config.duration;
      });
    }, 1000);

    // Simulate completion
    setTimeout(() => {
      setRunning(false);
      setProgress(100);
      setResult({
        actualTps: config.targetTps * (0.85 + Math.random() * 0.15),
        avgLatencyMs: 2 + Math.random() * 3,
        p99LatencyMs: 15 + Math.random() * 10,
        errorRate: Math.random() * 0.5,
        totalEvents: config.targetTps * config.duration,
        estimatedCost: estimateCost(),
      });

      addEvent({
        source: 'console',
        category: 'info',
        message: 'Load simulation complete',
      });
    }, config.duration * 1000);
  };

  const handleStop = () => {
    addEvent({
      source: 'console',
      category: 'api',
      message: 'Stopping load simulation',
    });
    setRunning(false);
  };

  return (
    <div className="loadsim-page">
      <div className="page-header">
        <h2>Load Simulation</h2>
      </div>

      <div className="loadsim-layout">
        <div className="config-panel">
          <h3>Configuration</h3>

          <div className="config-field">
            <label>Target TPS</label>
            <input
              type="range"
              min="100"
              max="50000"
              step="100"
              value={config.targetTps}
              onChange={(e) =>
                setConfig({ ...config, targetTps: parseInt(e.target.value) })
              }
              disabled={running}
            />
            <span className="field-value">{config.targetTps.toLocaleString()}</span>
          </div>

          <div className="config-field">
            <label>Duration (seconds)</label>
            <input
              type="range"
              min="10"
              max="300"
              step="10"
              value={config.duration}
              onChange={(e) =>
                setConfig({ ...config, duration: parseInt(e.target.value) })
              }
              disabled={running}
            />
            <span className="field-value">{config.duration}s</span>
          </div>

          <div className="config-field">
            <label>Payload Size (bytes)</label>
            <input
              type="range"
              min="64"
              max="4096"
              step="64"
              value={config.payloadSize}
              onChange={(e) =>
                setConfig({ ...config, payloadSize: parseInt(e.target.value) })
              }
              disabled={running}
            />
            <span className="field-value">{config.payloadSize}B</span>
          </div>

          <div className="config-field">
            <label>Concurrency</label>
            <input
              type="range"
              min="1"
              max="100"
              value={config.concurrency}
              onChange={(e) =>
                setConfig({ ...config, concurrency: parseInt(e.target.value) })
              }
              disabled={running}
            />
            <span className="field-value">{config.concurrency}</span>
          </div>

          <div className="estimate-box">
            <div className="estimate-row">
              <span>Total Events:</span>
              <span>{(config.targetTps * config.duration).toLocaleString()}</span>
            </div>
            <div className="estimate-row">
              <span>Estimated Cost:</span>
              <span className="cost">${estimateCost().toFixed(4)}</span>
            </div>
          </div>

          {running ? (
            <button className="btn-stop" onClick={handleStop}>
              ⏹ Stop Simulation
            </button>
          ) : (
            <button className="btn-start" onClick={handleStart}>
              ▶ Start Simulation
            </button>
          )}
        </div>

        <div className="results-panel">
          <h3>Results</h3>

          {running && (
            <div className="progress-section">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <span className="progress-text">{Math.round(progress)}% complete</span>
            </div>
          )}

          {result && (
            <div className="results-grid">
              <div className="result-card">
                <span className="result-value">
                  {Math.round(result.actualTps).toLocaleString()}
                </span>
                <span className="result-label">Actual TPS</span>
              </div>
              <div className="result-card">
                <span className="result-value">{result.avgLatencyMs.toFixed(1)}ms</span>
                <span className="result-label">Avg Latency</span>
              </div>
              <div className="result-card">
                <span className="result-value">{result.p99LatencyMs.toFixed(1)}ms</span>
                <span className="result-label">P99 Latency</span>
              </div>
              <div className="result-card">
                <span className="result-value">{result.errorRate.toFixed(2)}%</span>
                <span className="result-label">Error Rate</span>
              </div>
              <div className="result-card">
                <span className="result-value">
                  {result.totalEvents.toLocaleString()}
                </span>
                <span className="result-label">Total Events</span>
              </div>
              <div className="result-card">
                <span className="result-value cost">
                  ${result.estimatedCost.toFixed(4)}
                </span>
                <span className="result-label">Cost</span>
              </div>
            </div>
          )}

          {!running && !result && (
            <div className="empty-results">
              Configure and start a simulation to see results
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
